import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {getReadableLinkedInPrivacySetting, LinkedInPrivacySettings} from '@Components/social-media/post.vo';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {List} from '@Components/list';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Dropdown} from '@Components/dropdown-v2';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {noop} from '@Utils/general.util';
import {usePostPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import styles from './linkedin-privacy-selector.module.scss';
import {useAppDispatch} from '@/hooks';
import {updateLinkedInPublishingParamPrivacySettings} from '../../social-media-wizard-reducer';

interface LinkedInPrivacySelectorProps {
  account: SocialAccount;
  onSelectOption?: (selectedOption: LinkedInPrivacySettings) => void;
}

export function LinkedInPrivacySelector({account, onSelectOption = noop}: LinkedInPrivacySelectorProps): ReactElement<any> | null {
  const [selectedPrivacySettings, setSelectedPrivacySettings] = useState(LinkedInPrivacySettings.PUBLIC);
  const [dropdownLabel, setDropdownLabel] = useState(getReadableLinkedInPrivacySetting(selectedPrivacySettings));
  const publishingParamsForAccount = usePostPublishingParamsForAccount(account.id);
  const dispatch = useAppDispatch();

  const getLinkedInSpecificId = (privacySetting: LinkedInPrivacySettings): string => {
    return `linkedin-${account.id}-${privacySetting}`;
  };

  const getListItemByPrivacySetting = (privacySetting: LinkedInPrivacySettings): ListItemStorage => {
    return {
      id: getLinkedInSpecificId(privacySetting),
      isSelected: selectedPrivacySettings === privacySetting,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: getReadableLinkedInPrivacySetting(privacySetting),
      onClick(listId?: string, listItemId?: string): void {
        onSelectOption(privacySetting);
        if (listItemId === getLinkedInSpecificId(privacySetting)) {
          setDropdownLabel(getReadableLinkedInPrivacySetting(privacySetting));
          dispatch(
            updateLinkedInPublishingParamPrivacySettings({
              idSocialMediaAccount: account.id,
              privacySettings: privacySetting,
            })
          );
        }
      },
    };
  };

  const listItems: ListItemStorage[] = [
    getListItemByPrivacySetting(LinkedInPrivacySettings.PUBLIC),
    getListItemByPrivacySetting(LinkedInPrivacySettings.CONNECTIONS),
    getListItemByPrivacySetting(LinkedInPrivacySettings.LOGGED_IN),
  ];

  const getPrivacySelectorButton = (): ReactElement<any> => {
    return <DropdownDefaultSelectBox id={`linkedin-privacy-settings-selector-${account.id}`} size={DROPDOWN_SIZE.SMALL} text={dropdownLabel} className="_unmargin" />;
  };

  const getPrivacySettingsList = (): ReactElement<any> => {
    return (
      <List
        id={`linkedin-account-sort-dropdown-list-${account.id}`}
        className={styles.filterExceptionStyles}
        draggable={false}
        multiSelect={false}
        items={listItems}
        keepAtleastOneItemSelected
      />
    );
  };

  const getPrivacyOptions = (): ReactElement<any> => {
    return (
      <Dropdown
        id={`linkedin-social-posts-sort-${account.id}`}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        selector={getPrivacySelectorButton()}
        popup={getPrivacySettingsList()}
        popupClassName={styles.dropdown}
        popUpHasCustomWidth
      />
    );
  };

  useEffect(() => {
    if (!publishingParamsForAccount) {
      return;
    }

    if ('privacySettings' in publishingParamsForAccount) {
      const existingPrivacySetting = publishingParamsForAccount.privacySettings as LinkedInPrivacySettings;
      setDropdownLabel(getReadableLinkedInPrivacySetting(existingPrivacySetting));
      setSelectedPrivacySettings(existingPrivacySetting);
    }
  }, []);

  return (
    <div className="flex-h-row flex-content-between">
      <div className="flex-center">
        <Icon icon="icon-globe-2" className="spacing-m-r-2" type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
        <Text val={window.i18next.t('pmwjs_privacy')} size={TextSize.XSMALL} />
      </div>
      <div>{getPrivacyOptions()}</div>
    </div>
  );
}
